import { Injectable } from '@angular/core';

export interface CacheItem<T> {
  data: T;
  expiry: number;
}

@Injectable({
  providedIn: 'root',
})
export class SessionCacheService {
  // Default cache expiration time in milliseconds (5 minutes)
  private readonly DEFAULT_CACHE_EXPIRATION = 5 * 60 * 1000;

  // In-memory cache
  private cache: { [key: string]: CacheItem<any> } = {};

  constructor() {
    this.initializeFromSessionStorage();
  }

  /**
   * Initialize cache from sessionStorage on service creation
   */
  private initializeFromSessionStorage(): void {
    try {
      Object.keys(sessionStorage)
        .filter(key => key.startsWith('cache_'))
        .forEach(key => {
          const item = JSON.parse(sessionStorage.getItem(key));
          if (item && item.expiry > Date.now()) {
            this.cache[key] = item;
          } else {
            // Remove expired items
            sessionStorage.removeItem(key);
          }
        });
    } catch (e) {
      console.warn('Failed to initialize cache from sessionStorage', e);
    }
  }

  /**
   * Get cached item if available and not expired
   * @param key Cache key
   * @returns Cached data or null if not found or expired
   */
  get<T>(key: string): T | null {
    const cacheKey = `cache_${key}`;
    const cached = this.cache[cacheKey];
    
    if (cached && cached.expiry > Date.now()) {
      return cached.data;
    }
    
    // Check sessionStorage as fallback
    try {
      const storedItem = sessionStorage.getItem(cacheKey);
      if (storedItem) {
        const parsedItem = JSON.parse(storedItem) as CacheItem<T>;
        if (parsedItem.expiry > Date.now()) {
          // Update in-memory cache
          this.cache[cacheKey] = parsedItem;
          return parsedItem.data;
        } else {
          // Remove expired item
          sessionStorage.removeItem(cacheKey);
        }
      }
    } catch (e) {
      // Silently fail if sessionStorage is not available
    }
    
    return null;
  }

  /**
   * Set item in cache with expiration time
   * @param key Cache key
   * @param data Data to cache
   * @param cacheTime Cache duration in milliseconds (default: 5 minutes)
   */
  set<T>(key: string, data: T, cacheTime: number = this.DEFAULT_CACHE_EXPIRATION): void {
    const cacheKey = `cache_${key}`;
    const cacheObject: CacheItem<T> = {
      data,
      expiry: Date.now() + cacheTime
    };
    
    // Update in-memory cache
    this.cache[cacheKey] = cacheObject;
    
    // Update sessionStorage
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheObject));
    } catch (e) {
      console.warn(`Failed to cache item '${key}' in sessionStorage`, e);
    }
  }

  /**
   * Remove item from cache
   * @param key Cache key
   */
  remove(key: string): void {
    const cacheKey = `cache_${key}`;
    delete this.cache[cacheKey];
    
    try {
      sessionStorage.removeItem(cacheKey);
    } catch (e) {
      // Silently fail if sessionStorage is not available
    }
  }

  /**
   * Clear all cached items or items with a specific prefix
   * @param keyPrefix Optional prefix to clear only specific items
   */
  clear(keyPrefix?: string): void {
    try {
      if (keyPrefix) {
        // Clear only items with specific prefix
        const prefixWithCachePrefix = `cache_${keyPrefix}`;
        
        // Clear from in-memory cache
        Object.keys(this.cache)
          .filter(key => key.startsWith(prefixWithCachePrefix))
          .forEach(key => delete this.cache[key]);
        
        // Clear from sessionStorage
        Object.keys(sessionStorage)
          .filter(key => key.startsWith(prefixWithCachePrefix))
          .forEach(key => sessionStorage.removeItem(key));
      } else {
        // Clear all cache items
        this.cache = {};
        
        // Clear all cache items from sessionStorage
        Object.keys(sessionStorage)
          .filter(key => key.startsWith('cache_'))
          .forEach(key => sessionStorage.removeItem(key));
      }
    } catch (e) {
      console.warn('Failed to clear cache', e);
    }
  }

  /**
   * Get the default cache expiration time
   */
  getDefaultExpiration(): number {
    return this.DEFAULT_CACHE_EXPIRATION;
  }
}
